/**
 * Sysadmin Catalog → Skill Detail (PR-I).
 *
 * 5 tabs: Overview · Composition · Versions · Assignments · Audit.
 *
 * Lifecycle action bar at the top: Publish / Unpublish (deprecated|retired)
 * / Restore-to-draft. Publish is gated by the API's
 * dangling-reference validator — the UI surfaces the 422 details.
 *
 * Mirrors models.$id pattern + catalog.prompts edit drawer.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Sparkles, ArrowLeft, AlertTriangle, ShieldCheck,
  Eye, Trash2, X, Loader2, BookText, Wrench, FunctionSquare,
  History, UserCheck, ScrollText, Plus, MinusCircle, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  SkillContentEditor,
  type SkillContentDraft,
} from '@/components/skill-editor-shared/SkillContentEditor';
import {
  useCatalogSkill,
  useUpdateCatalogSkill,
  useDeleteCatalogSkill,
  usePublishCatalogSkill,
  useUnpublishCatalogSkill,
  useCatalogSkillAssignments,
  useAssignCatalogSkill,
  useRevokeCatalogSkillAssignment,
  type CatalogSkillDetail,
  type SkillStatus,
} from '@/lib/use-sysadmin-catalog-skills';
import { useSysAdminSystemTools } from '@/lib/use-sysadmin-system-catalog';
import { useWrapperFunctions } from '@/lib/use-argus-models';
import { useTenants } from '@/lib/use-tenants';

export const Route = createFileRoute('/sysadmin/catalog/skills/$id')({
  component: SysadminCatalogSkillDetailPage,
});

function statusPillClass(status: SkillStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

function SysadminCatalogSkillDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useCatalogSkill(id);
  const skill = data?.data;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteCatalogSkill();

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading skill…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{error?.message ?? 'Skill not found.'}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await del.mutateAsync({ id });
      toast.success(`Deleted "${skill.displayName}"`);
      navigate({ to: '/sysadmin/catalog/skills' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header + back */}
      <div className="flex items-start gap-3">
        <Link
          to="/sysadmin/catalog/skills"
          className="text-muted-foreground hover:text-foreground p-1.5 rounded -ml-1.5"
          title="Back to skill library"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <Sparkles className="h-7 w-7 text-muted-foreground mt-0.5" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {skill.displayName}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="font-mono text-[11px]">
              {skill.slug}
            </Badge>
            <Badge
              variant="outline"
              className={cn('text-[10px] capitalize border', statusPillClass(skill.status))}
            >
              {skill.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {skill.category}
            </Badge>
            <span className="text-xs text-muted-foreground tabular-nums">
              v{skill.version}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {skill.assignmentCount} tenant{skill.assignmentCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      <LifecycleActionBar skill={skill} onDelete={() => setConfirmDelete(true)} />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">
            <BookText className="h-3.5 w-3.5 mr-1.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="composition">
            <Wrench className="h-3.5 w-3.5 mr-1.5" />
            Composition
          </TabsTrigger>
          <TabsTrigger value="versions">
            <History className="h-3.5 w-3.5 mr-1.5" />
            Versions
          </TabsTrigger>
          <TabsTrigger value="assignments">
            <UserCheck className="h-3.5 w-3.5 mr-1.5" />
            Assignments
          </TabsTrigger>
          <TabsTrigger value="audit">
            <ScrollText className="h-3.5 w-3.5 mr-1.5" />
            Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab skill={skill} />
        </TabsContent>

        <TabsContent value="composition">
          <CompositionTab skill={skill} />
        </TabsContent>

        <TabsContent value="versions">
          <VersionsTab skill={skill} />
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentsTab skill={skill} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab skill={skill} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete skill</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently soft-delete{' '}
              <code className="bg-muted px-1 rounded">{skill.slug}</code>?
              This is only allowed for draft or retired skills.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete skill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Lifecycle action bar ─────────────────────────────────────────────────

function LifecycleActionBar({
  skill,
  onDelete,
}: {
  skill: CatalogSkillDetail;
  onDelete: () => void;
}) {
  const publish = usePublishCatalogSkill();
  const unpublish = useUnpublishCatalogSkill();
  const [unpublishDialog, setUnpublishDialog] = useState<{
    nextStatus: 'draft' | 'deprecated' | 'retired';
  } | null>(null);
  const [unpublishReason, setUnpublishReason] = useState('');

  const handlePublish = async () => {
    try {
      await publish.mutateAsync({ id: skill.id });
      toast.success(`Published "${skill.displayName}"`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // The 422 dangling-reference response carries actionable detail.
      toast.error('Publish blocked', {
        description: msg,
      });
    }
  };

  const handleUnpublish = async () => {
    if (!unpublishDialog || !unpublishReason.trim()) return;
    try {
      await unpublish.mutateAsync({
        id: skill.id,
        reason: unpublishReason.trim(),
        nextStatus: unpublishDialog.nextStatus,
      });
      toast.success(`Moved "${skill.displayName}" to ${unpublishDialog.nextStatus}`);
      setUnpublishDialog(null);
      setUnpublishReason('');
    } catch (err) {
      toast.error('Unpublish failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const canPublish = skill.status === 'draft' || skill.status === 'deprecated';
  const canDeprecate = skill.status === 'published';
  const canRetire =
    skill.status === 'published' || skill.status === 'deprecated';
  const canRestoreDraft =
    (skill.status === 'deprecated' || skill.status === 'retired') &&
    skill.assignmentCount === 0;
  const canDelete = skill.status === 'draft' || skill.status === 'retired';

  return (
    <>
      <Card className="bg-muted/30">
        <CardContent className="py-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-2">
            Lifecycle:
          </span>
          {canPublish && (
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publish.isPending}
              title="Publish — composition is validated against active tools and functions"
            >
              {publish.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              )}
              Publish
            </Button>
          )}
          {/*
            Issue #746 — PR-U1 (CATALOG-MIGRATION-DESIGN §6.11) replaced
            the deprecated -> retired -> restore-to-draft lifecycle with
            a 2-state Q7 model (draft|published) plus `enabled` for
            soft-disable and `deleted_at` for soft-delete. The backend
            POST /catalog-skills/:id/unpublish endpoint now returns
            410 Gone, so the Deprecate / Retire / Restore-to-draft
            buttons were dead controls. Soft-disable is the Enabled
            toggle on details; soft-delete is the Delete button below.
            `canDeprecate` / `canRetire` / `canRestoreDraft` gates are
            kept for future re-introduction if the lifecycle is ever
            re-expanded.
          */}
          <div className="flex-1" />
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="text-destructive hover:bg-destructive/10"
              title="Soft-delete — only draft or retired"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={unpublishDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setUnpublishDialog(null);
            setUnpublishReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move to {unpublishDialog?.nextStatus}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Provide a reason for the audit log. This is required.
              {unpublishDialog?.nextStatus === 'draft' && (
                <>
                  <br />
                  <strong className="text-amber-600">
                    Note: Restoring to draft requires zero tenant assignments.
                  </strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={unpublishReason}
            onChange={(e) => setUnpublishReason(e.target.value)}
            placeholder="Reason (e.g. replaced by skill-v2, schema breaking change, etc.)"
            rows={3}
            className="text-sm"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!unpublishReason.trim() || unpublish.isPending}
              onClick={handleUnpublish}
              className={cn(
                unpublishDialog?.nextStatus === 'retired' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
            >
              {unpublish.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────
//
// PR-W22B (2026-05-23) — closes #587. The Identity / Description /
// Instruction body / Skill Helper sections are now rendered by the shared
// <SkillContentEditor mode="edit"> in
// `@/components/skill-editor-shared/SkillContentEditor`. This tab owns the
// edit chrome, mutation, draft state, and the readOnly gate (retired
// skills). Same shared editor is mounted by the catalog list's Create
// drawer with mode="create".
//
// Lifecycle action bar + Composition / Versions / Assignments / Audit
// tabs are unchanged — those surfaces are catalog-specific and not part
// of the shared editor body.

function OverviewTab({ skill }: { skill: CatalogSkillDetail }) {
  const baseline: SkillContentDraft = {
    slug: skill.slug,
    displayName: skill.displayName,
    description: skill.description,
    instructions: skill.instructions,
    category: skill.category,
  };
  const [draft, setDraft] = useState<SkillContentDraft>(baseline);
  const update = useUpdateCatalogSkill();

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: skill.id,
        displayName: draft.displayName,
        description: draft.description,
        instructions: draft.instructions,
        category: draft.category,
      });
      toast.success('Saved changes');
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err; // rethrow so the shared editor's await rejects
    }
  };

  const isReadOnly = skill.status === 'retired';

  return (
    <SkillContentEditor
      mode="edit"
      draft={draft}
      onDraftChange={setDraft}
      baseline={baseline}
      onSave={handleSave}
      saving={update.isPending}
      readOnly={isReadOnly}
      immutableSlug
      toolDependencies={skill.toolDependencies}
      functionDependencies={skill.functionDependencies}
    />
  );
}

// ─── Composition tab ──────────────────────────────────────────────────────

function CompositionTab({ skill }: { skill: CatalogSkillDetail }) {
  const update = useUpdateCatalogSkill();
  const [pickerKind, setPickerKind] = useState<'tools' | 'functions' | null>(null);

  const handleRemoveTool = async (toolId: string) => {
    try {
      await update.mutateAsync({
        id: skill.id,
        toolDependencies: skill.toolDependencies.filter((id) => id !== toolId),
      });
      toast.success('Removed tool reference');
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleRemoveFunction = async (fnId: string) => {
    try {
      await update.mutateAsync({
        id: skill.id,
        functionDependencies: skill.functionDependencies.filter(
          (id) => id !== fnId,
        ),
      });
      toast.success('Removed function reference');
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isReadOnly = skill.status === 'retired';

  return (
    <div className="space-y-4">
      {(skill.composition.danglingToolIds.length > 0 ||
        skill.composition.danglingFunctionIds.length > 0) && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="py-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
            <div>
              <div className="font-medium text-destructive">
                Dangling references detected
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {skill.composition.danglingToolIds.length > 0 && (
                  <div>
                    {skill.composition.danglingToolIds.length} tool{' '}
                    reference(s) point to deleted rows.
                  </div>
                )}
                {skill.composition.danglingFunctionIds.length > 0 && (
                  <div>
                    {skill.composition.danglingFunctionIds.length} function{' '}
                    reference(s) point to deleted rows.
                  </div>
                )}
                Remove or replace before publishing.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Tools ({skill.composition.tools.length})
            </CardTitle>
            <CardDescription>
              Tools the skill calls. On publish, every tool must be active.
            </CardDescription>
          </div>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerKind('tools')}
              title="Add tool reference"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add tool
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {skill.composition.tools.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No tool references yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-44">Slug</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {!isReadOnly && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {skill.composition.tools.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{t.displayName}</TableCell>
                    <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          t.isActive
                            ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                            : 'border-amber-500/30 text-amber-700 dark:text-amber-400',
                        )}
                      >
                        {t.isActive ? 'active' : 'inactive'}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveTool(t.id)}
                          title="Remove reference"
                        >
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FunctionSquare className="h-4 w-4" />
              Functions ({skill.composition.functions.length})
            </CardTitle>
            <CardDescription>
              Argus wrapper functions the skill calls.
            </CardDescription>
          </div>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerKind('functions')}
              title="Add function reference"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add function
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {skill.composition.functions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No function references yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-56">Function key</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {!isReadOnly && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {skill.composition.functions.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="text-sm">{f.displayName}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {f.functionKey}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          f.isActive
                            ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                            : 'border-amber-500/30 text-amber-700 dark:text-amber-400',
                        )}
                      >
                        {f.isActive ? 'active' : 'inactive'}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveFunction(f.id)}
                          title="Remove reference"
                        >
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CompositionPicker
        kind={pickerKind}
        skill={skill}
        onClose={() => setPickerKind(null)}
      />
    </div>
  );
}

// ─── Composition picker drawer (tools / functions) ────────────────────────

function CompositionPicker({
  kind,
  skill,
  onClose,
}: {
  kind: 'tools' | 'functions' | null;
  skill: CatalogSkillDetail;
  onClose: () => void;
}) {
  const tools = useSysAdminSystemTools();
  const fns = useWrapperFunctions();
  const update = useUpdateCatalogSkill();
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    if (kind === 'tools') {
      const all = tools.data?.items ?? [];
      const existing = new Set(skill.toolDependencies);
      const candidates = all.filter((t) => !existing.has(t.id));
      if (!search) return candidates;
      const q = search.toLowerCase();
      return candidates.filter(
        (t) =>
          t.slug.toLowerCase().includes(q) ||
          t.displayName.toLowerCase().includes(q),
      );
    }
    if (kind === 'functions') {
      const all = fns.data?.items ?? [];
      const existing = new Set(skill.functionDependencies);
      const candidates = all.filter((f) => !existing.has(f.id));
      if (!search) return candidates;
      const q = search.toLowerCase();
      return candidates.filter(
        (f) =>
          f.functionKey.toLowerCase().includes(q) ||
          f.displayName.toLowerCase().includes(q),
      );
    }
    return [];
  }, [kind, tools.data, fns.data, skill, search]);

  const handleAdd = async (id: string) => {
    try {
      if (kind === 'tools') {
        await update.mutateAsync({
          id: skill.id,
          toolDependencies: [...skill.toolDependencies, id],
        });
        toast.success('Added tool reference');
      } else if (kind === 'functions') {
        await update.mutateAsync({
          id: skill.id,
          functionDependencies: [...skill.functionDependencies, id],
        });
        toast.success('Added function reference');
      }
    } catch (err) {
      toast.error('Add failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isLoading = kind === 'tools' ? tools.isLoading : fns.isLoading;
  const error = kind === 'tools' ? tools.error : fns.error;

  return (
    <Sheet open={kind !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Add {kind === 'tools' ? 'tool' : 'function'} reference
          </SheetTitle>
          <SheetDescription>
            Multi-select. References are added one at a time so the skill
            row's version increments cleanly.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder={`Search ${kind ?? ''}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>

          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="py-6 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              {error.message}
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {search
                ? `No matches for "${search}".`
                : 'Nothing left to add — every available item is already referenced.'}
            </div>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {items.map((it: any) => {
                const id = it.id as string;
                const slug = (it.slug ?? it.functionKey) as string;
                const name = it.displayName as string;
                const active = (it.isActive ?? true) as boolean;
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between border rounded p-2 hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {slug}
                      </div>
                    </div>
                    {!active && (
                      <Badge variant="outline" className="text-[10px] mr-2">
                        inactive
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleAdd(id)}
                      disabled={update.isPending}
                      title="Add reference"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Versions tab ─────────────────────────────────────────────────────────

function VersionsTab({ skill }: { skill: CatalogSkillDetail }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Version history</CardTitle>
        <CardDescription>
          Every PATCH snapshots the prior row to{' '}
          <code className="font-mono text-[10px] bg-muted px-1 rounded">
            system_skill_versions
          </code>
          . Current row is v{skill.version}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {skill.versions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No prior versions yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Version</TableHead>
                <TableHead>Changed at</TableHead>
                <TableHead>Changed by (admin id)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {skill.versions.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="tabular-nums">v{v.version}</TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {new Date(v.changedAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">
                    {v.changedByAdmin ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Assignments tab ──────────────────────────────────────────────────────

function AssignmentsTab({ skill }: { skill: CatalogSkillDetail }) {
  const assignments = useCatalogSkillAssignments(skill.id);
  const [showAssign, setShowAssign] = useState(false);
  const revoke = useRevokeCatalogSkillAssignment();
  const [confirmRevoke, setConfirmRevoke] = useState<{
    id: string;
    orgName: string;
  } | null>(null);

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    try {
      await revoke.mutateAsync({
        id: confirmRevoke.id,
        systemSkillId: skill.id,
      });
      toast.success(`Revoked from "${confirmRevoke.orgName}"`);
      setConfirmRevoke(null);
    } catch (err) {
      toast.error('Revoke failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const canAssign = skill.status === 'published';

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base">Tenant assignments</CardTitle>
            <CardDescription>
              Tenants only see this skill when an active assignment row
              exists AND status='published'. Idempotent assign on (skill,
              tenant).
              {!canAssign && (
                <span className="block mt-1 text-amber-600 dark:text-amber-500">
                  Skill must be in <span className="font-mono">published</span>{' '}
                  status to assign.
                </span>
              )}
            </CardDescription>
          </div>
          <Button
            size="sm"
            disabled={!canAssign}
            onClick={() => setShowAssign(true)}
            title={
              canAssign
                ? 'Assign to a tenant'
                : 'Publish the skill first'
            }
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Assign tenant
          </Button>
        </CardHeader>
        <CardContent>
          {assignments.isLoading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
              Loading…
            </div>
          ) : assignments.error ? (
            <div className="py-6 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              {assignments.error.message}
            </div>
          ) : (assignments.data?.items ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No tenant assignments yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-44">Assigned at</TableHead>
                  <TableHead className="w-16 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(assignments.data?.items ?? []).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm">
                      {a.organizationName ?? a.organizationId}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.notes ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(a.assignedAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          setConfirmRevoke({
                            id: a.id,
                            orgName: a.organizationName ?? a.organizationId,
                          })
                        }
                        title="Revoke assignment"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AssignDrawer
        skill={skill}
        open={showAssign}
        onOpenChange={setShowAssign}
      />

      <AlertDialog
        open={confirmRevoke !== null}
        onOpenChange={(open) => !open && setConfirmRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove access to{' '}
              <code className="bg-muted px-1 rounded">{skill.slug}</code>{' '}
              from{' '}
              <strong>{confirmRevoke?.orgName ?? ''}</strong>? Their
              tenant users will stop seeing this skill on the next page
              load. Re-assigning is supported (idempotent).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRevoke}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AssignDrawer({
  skill,
  open,
  onOpenChange,
}: {
  skill: CatalogSkillDetail;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const tenants = useTenants({ status: 'active' });
  const assignments = useCatalogSkillAssignments(skill.id);
  const assign = useAssignCatalogSkill();

  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [notes, setNotes] = useState('');

  const candidates = useMemo(() => {
    const existing = new Set(
      (assignments.data?.items ?? []).map((a) => a.organizationId),
    );
    return (tenants.data?.tenants ?? []).filter((t) => !existing.has(t.id));
  }, [tenants.data, assignments.data]);

  const handleAssign = async () => {
    if (!selectedOrg) return;
    try {
      const result = await assign.mutateAsync({
        systemSkillId: skill.id,
        organizationId: selectedOrg,
        notes: notes.trim() || undefined,
      });
      if (result.alreadyExisted) {
        toast.info('Already assigned to this tenant.');
      } else {
        toast.success('Assigned');
      }
      setSelectedOrg('');
      setNotes('');
      onOpenChange(false);
    } catch (err) {
      toast.error('Assign failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Assign tenant</SheetTitle>
          <SheetDescription>
            The tenant will see this skill at{' '}
            <code className="font-mono text-[10px] bg-muted px-1 rounded">
              GET /v1/skills/system
            </code>
            .
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Tenant</Label>
            {tenants.isLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tenant…" />
                </SelectTrigger>
                <SelectContent>
                  {candidates.length === 0 ? (
                    <div className="text-sm text-muted-foreground p-2">
                      Every active tenant is already assigned.
                    </div>
                  ) : (
                    candidates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this skill is being assigned to this tenant."
              rows={3}
              className="text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedOrg('');
                setNotes('');
                onOpenChange(false);
              }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              disabled={!selectedOrg || assign.isPending}
              onClick={handleAssign}
            >
              {assign.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <UserCheck className="h-3.5 w-3.5 mr-1.5" />
              )}
              Assign
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Audit tab ────────────────────────────────────────────────────────────

function AuditTab({ skill }: { skill: CatalogSkillDetail }) {
  // The platform audit firehose is at /admin/platform/audit/sysadmin-actions.
  // Rather than re-build a paginated audit view here, deep-link to the
  // existing sysadmin Audit module pre-filtered to this skill's resource id.
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Audit</CardTitle>
        <CardDescription>
          Every mutation on this skill (create / update / publish /
          unpublish / delete / assign / revoke) is hash-chained into the
          platform audit log.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Published at</div>
            <div className="tabular-nums mt-0.5">
              {skill.publishedAt
                ? new Date(skill.publishedAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Published by</div>
            <div className="font-mono text-xs mt-0.5">
              {skill.publishedBy ?? '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Unpublished at</div>
            <div className="tabular-nums mt-0.5">
              {skill.unpublishedAt
                ? new Date(skill.unpublishedAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Unpublished by</div>
            <div className="font-mono text-xs mt-0.5">
              {skill.unpublishedBy ?? '—'}
            </div>
          </div>
        </div>
        {skill.unpublishReason && (
          <div className="border rounded-md p-3 bg-muted/30">
            <div className="text-xs text-muted-foreground mb-1">
              Unpublish reason
            </div>
            <div className="text-sm">{skill.unpublishReason}</div>
          </div>
        )}
        <div className="pt-2">
          <Link
            to="/sysadmin/audit"
            search={{ resourceId: skill.id }}
            className="inline-flex items-center text-sm text-primary hover:underline"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View full audit history in sysadmin Audit module
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
